import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/api/client";
import { Card, Badge, Spinner, EmptyState } from "@/components/ui";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Modal from "@/components/ui/Modal";
import toast from "react-hot-toast";
import clsx from "clsx";

const CATEGORIES = ["Mathe", "Deutsch", "Sachkunde", "Logik", "Geografie", "Englisch", "Geschichte", "Informatik"];
const EMOJIS     = ["📐","📝","🌍","🧩","🗺️","🇬🇧","📜","💻","🔬","🎨","🎵","⚽","🚀","🌿"];

function QuestionEditor({ questions, onChange }) {
  const addQuestion = () => onChange([...questions, { text: "", options: ["", "", "", ""], correct: 0, explain: "" }]);

  const updateQ = (idx, field, val) => {
    const qs = [...questions];
    qs[idx] = { ...qs[idx], [field]: val };
    onChange(qs);
  };
  const updateOpt = (qIdx, oIdx, val) => {
    const qs = [...questions];
    const opts = [...qs[qIdx].options];
    opts[oIdx] = val;
    qs[qIdx] = { ...qs[qIdx], options: opts };
    onChange(qs);
  };
  const removeQ = (idx) => onChange(questions.filter((_, i) => i !== idx));

  return (
    <div className="space-y-4">
      {questions.map((q, qi) => (
        <Card key={qi} className="p-4 border-violet-500/20">
          <div className="flex items-center justify-between mb-3">
            <p className="font-black text-sm text-violet-400">Frage {qi + 1}</p>
            <button onClick={() => removeQ(qi)} className="text-red-400 text-xs hover:text-red-300 font-bold">🗑️ Löschen</button>
          </div>
          <textarea
            value={q.text}
            onChange={(e) => updateQ(qi, "text", e.target.value)}
            placeholder="Frage eingeben..."
            className="w-full bg-nexus-s2 border border-nexus-border rounded-xl p-3 text-sm text-white placeholder-gray-500 resize-none h-16 mb-3 focus:outline-none focus:border-violet-500"
          />
          <div className="space-y-2 mb-3">
            {q.options.map((opt, oi) => (
              <div key={oi} className="flex items-center gap-2">
                <button
                  onClick={() => updateQ(qi, "correct", oi)}
                  className={clsx(
                    "w-7 h-7 rounded-lg flex-shrink-0 border-2 font-black text-xs transition-all",
                    q.correct === oi
                      ? "bg-green-500 border-green-400 text-white"
                      : "bg-nexus-s2 border-nexus-border text-gray-500 hover:border-green-500/50"
                  )}
                >
                  {["A","B","C","D"][oi]}
                </button>
                <input
                  value={opt}
                  onChange={(e) => updateOpt(qi, oi, e.target.value)}
                  placeholder={`Antwort ${["A","B","C","D"][oi]}`}
                  className="flex-1 bg-nexus-s2 border border-nexus-border rounded-xl px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-violet-500"
                />
              </div>
            ))}
          </div>
          <input
            value={q.explain || ""}
            onChange={(e) => updateQ(qi, "explain", e.target.value)}
            placeholder="💡 Erklärung (optional)"
            className="w-full bg-nexus-s2 border border-nexus-border rounded-xl px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-violet-500"
          />
        </Card>
      ))}
      <Button full variant="secondary" onClick={addQuestion}>+ Frage hinzufügen</Button>
    </div>
  );
}

export default function QuizBuilder() {
  const qc = useQueryClient();
  const [modal, setModal] = useState(false); // false | "new" | id
  const [form, setForm] = useState({
    title: "", emoji: "📝", category: "Mathe", difficulty: 2,
    xpReward: 100, coinsReward: 50, questions: [],
  });
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["quizzes"],
    queryFn: () => api.get("/quizzes").then((r) => r.data),
  });

  const save = useMutation({
    mutationFn: () =>
      modal === "new"
        ? api.post("/quizzes", form)
        : api.put(`/quizzes/${modal}`, form),
    onSuccess: () => {
      toast.success(modal === "new" ? "Quiz erstellt! 📚" : "Quiz gespeichert!");
      qc.invalidateQueries({ queryKey: ["quizzes"] });
      setModal(false);
    },
    onError: (err) => toast.error(err.response?.data?.error || "Fehler"),
  });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/quizzes/${id}`),
    onSuccess: () => {
      toast.success("Quiz gelöscht");
      qc.invalidateQueries({ queryKey: ["quizzes"] });
      setDeleteTarget(null);
    },
    onError: (err) => toast.error(err.response?.data?.error || "Fehler"),
  });

  const openEdit = (q) => {
    setForm({
      title: q.title, emoji: q.emoji, category: q.category,
      difficulty: q.difficulty, xpReward: q.xpReward, coinsReward: q.coinsReward,
      questions: q.questions?.map((qu) => ({
        ...qu,
        options: typeof qu.options === "string" ? JSON.parse(qu.options) : qu.options,
      })) || [],
    });
    setModal(q.id);
  };

  const openNew = () => {
    setForm({ title: "", emoji: "📝", category: "Mathe", difficulty: 2, xpReward: 100, coinsReward: 50, questions: [] });
    setModal("new");
  };

  const quizzes = data?.quizzes || [];

  return (
    <div className="space-y-5 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-black">📚 Quiz-Editor</h1>
          <p className="text-xs text-gray-400 mt-0.5">{quizzes.length} Quizze</p>
        </div>
        <Button size="sm" onClick={openNew}>+ Neues Quiz</Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center pt-10"><Spinner size="lg" /></div>
      ) : quizzes.length === 0 ? (
        <EmptyState icon="📚" title="Keine Quizze" desc="Erstelle dein erstes Quiz!" action={<Button onClick={openNew}>+ Quiz erstellen</Button>} />
      ) : (
        <div className="space-y-2">
          {quizzes.map((q) => (
            <Card key={q.id} className="p-3.5">
              <div className="flex items-center gap-3">
                <span className="text-2xl flex-shrink-0">{q.emoji}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm">{q.title}</p>
                  <div className="flex gap-1.5 mt-1 flex-wrap">
                    <Badge color="purple">{q.category}</Badge>
                    <span className="text-[10px] text-gray-500">{q.questions?.length} Fragen</span>
                    <span className="text-[10px] text-violet-400">+{q.xpReward} XP</span>
                    <span className="text-[10px] text-gray-500">{q._count?.attempts || 0}× gespielt</span>
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button size="sm" variant="secondary" onClick={() => openEdit(q)}>✏️</Button>
                  <Button size="sm" variant="danger" onClick={() => setDeleteTarget(q)}>🗑️</Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Quiz editor modal */}
      <Modal open={!!modal} onClose={() => setModal(false)} title={modal === "new" ? "📚 Neues Quiz" : "✏️ Quiz bearbeiten"}>
        <div className="space-y-4">
          <Input
            label="Titel"
            value={form.title}
            onChange={(v) => setForm((f) => ({ ...f, title: v }))}
            placeholder="Quiz-Titel..."
          />

          {/* Emoji picker */}
          <div>
            <p className="text-xs font-bold text-gray-400 mb-2">Emoji</p>
            <div className="flex flex-wrap gap-2">
              {EMOJIS.map((e) => (
                <button
                  key={e}
                  onClick={() => setForm((f) => ({ ...f, emoji: e }))}
                  className={clsx(
                    "w-9 h-9 rounded-lg text-lg border-2 transition-all",
                    form.emoji === e ? "border-violet-500 bg-violet-500/20" : "border-nexus-border bg-nexus-s2"
                  )}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>

          {/* Category */}
          <div>
            <p className="text-xs font-bold text-gray-400 mb-2">Kategorie</p>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  onClick={() => setForm((f) => ({ ...f, category: c }))}
                  className={clsx(
                    "px-3 py-1.5 rounded-xl text-xs font-bold border transition-all",
                    form.category === c ? "bg-violet-600 border-violet-500 text-white" : "bg-nexus-s2 border-nexus-border text-gray-400"
                  )}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {/* Difficulty */}
          <div>
            <p className="text-xs font-bold text-gray-400 mb-2">Schwierigkeit</p>
            <div className="flex gap-2">
              {[1,2,3,4,5].map((d) => (
                <button
                  key={d}
                  onClick={() => setForm((f) => ({ ...f, difficulty: d }))}
                  className={clsx(
                    "flex-1 py-2 rounded-xl text-sm border-2 font-bold transition-all",
                    form.difficulty === d ? "bg-violet-600 border-violet-500 text-white" : "bg-nexus-s2 border-nexus-border text-gray-400"
                  )}
                >
                  {"⭐".repeat(d)}
                </button>
              ))}
            </div>
          </div>

          {/* Rewards */}
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="XP Belohnung"
              type="number"
              value={String(form.xpReward)}
              onChange={(v) => setForm((f) => ({ ...f, xpReward: Number(v) }))}
            />
            <Input
              label="Coins Belohnung"
              type="number"
              value={String(form.coinsReward)}
              onChange={(v) => setForm((f) => ({ ...f, coinsReward: Number(v) }))}
            />
          </div>

          {/* Questions */}
          <div>
            <p className="text-xs font-bold text-gray-400 mb-2">Fragen ({form.questions.length})</p>
            <QuestionEditor
              questions={form.questions}
              onChange={(qs) => setForm((f) => ({ ...f, questions: qs }))}
            />
          </div>

          <Button full loading={save.isPending} onClick={() => save.mutate()}>
            💾 Speichern
          </Button>
        </div>
      </Modal>

      {/* Delete confirm modal */}
      <Modal open={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Quiz löschen?">
        {deleteTarget && (
          <div className="space-y-4">
            <p className="text-sm text-gray-300 text-center">
              Möchtest du <strong>{deleteTarget.title}</strong> wirklich löschen?<br />
              Alle Versuche ({deleteTarget._count?.attempts || 0}) gehen verloren!
            </p>
            <div className="flex gap-2">
              <Button full variant="danger" loading={del.isPending} onClick={() => del.mutate(deleteTarget.id)}>
                🗑️ Löschen
              </Button>
              <Button full variant="secondary" onClick={() => setDeleteTarget(null)}>Abbrechen</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
